import { DakokuDataFull, DakokuInfoShinsei, ShinseiInfo } from "@/types/Dakoku";
import { DakokuSyuusei, HayabanOsoban } from "@/types/Shinsei";
import { DateUtil } from "@/utils/DateUtil";
import { isString } from "lodash";
import { onMounted, reactive, ref, Ref } from "vue";
import OptionConst from "@/const/Option";
import { useForm } from '@/composable/shinsei/typelist/form';
import { DialogService } from "@/service/DialogService";
import { useInit } from "@/composable/shinsei/hayaban_osoban/init";
import dayjs from "dayjs";

export interface DakokuSyuuseiData {
  before: {
    koyou_keiyaku_time_id: number | null,
    dakoku_id: number | null,
    dakoku_date: string,
    syukkin_datetime: string,
    taikin_datetime: string,
    gaisyutsu_datetime: string,
    modori_datetime: string,
    kyuukei_times: string | null
  },
  after: {
    koyou_keiyaku_time_id: number | null,
    dakoku_id: number | null,
    dakoku_date: string,
    syukkin_datetime: string,
    taikin_datetime: string,
    gaisyutsu_datetime: string,
    modori_datetime: string,
    kyuukei_times: string | null
  }
}

export function useDakokuSyuuseiModal() {
  const { getApprover, flow } = useForm()
  const { hayabanOsoban } = useInit()
  const dakokuSyuuseiModalRef = ref()

  const syuuseiData: DakokuSyuuseiData = reactive({
    before: {
      koyou_keiyaku_time_id: null,
      dakoku_id: null,
      dakoku_date: '',
      syukkin_datetime: '',
      taikin_datetime: '',
      gaisyutsu_datetime: '',
      modori_datetime: '',
      kyuukei_times: '',
    },
    after: {
      koyou_keiyaku_time_id: null,
      dakoku_id: null,
      dakoku_date: '',
      syukkin_datetime: '',
      taikin_datetime: '',
      gaisyutsu_datetime: '',
      modori_datetime: '',
      kyuukei_times: '',
    }
  })

  let shinseiData:Ref<DakokuInfoShinsei> = ref({
    hayabanOsoban: [],
    koukyuu: [],
    jikangai: [],
    kyuukei: [],
    furikyuu: [],
    yuukyuu: [],
    kekkin: [],
    soutai: [],
    chikoku: [],
    nittyoku: [],
    kibiki: [],
    hannichiHosei: [],
    syuusei: [],
  })

  onMounted(async() => {
    await getApprover(OptionConst.SHINSEI_CODE.DAKOKU_SYUUSEI, false)
  })

  const hasFlow = () =>{
    if(flow.value.length <= 0){
      DialogService.warning({
        title: '承認グループがありません',
        text: '先に承認グループを登録してください。',
      })
      return true
    }
    return false
  }

  const setData = async (syuuseiTarget: DakokuDataFull | null, date: string) => {
    const formattedHour = (datetime: Date | string | null) => {
      if (!datetime) {
        return ''
      }
      if (isString(datetime)) {
        datetime = new Date(datetime.replace(/-/g,"/"));
      }
      return datetime.toLocaleTimeString()
    }

    syuuseiData.after.dakoku_date = syuuseiData.before.dakoku_date = date
    if(syuuseiTarget){
      syuuseiData.after.koyou_keiyaku_time_id = syuuseiData.before.koyou_keiyaku_time_id = syuuseiTarget.koyou_keiyaku_time_id
      syuuseiData.after.dakoku_id = syuuseiData.before.dakoku_id = syuuseiTarget.dakoku_id
      syuuseiData.after.syukkin_datetime = syuuseiData.before.syukkin_datetime = formattedHour(syuuseiTarget.syukkin_datetime)
      syuuseiData.after.taikin_datetime = syuuseiData.before.taikin_datetime = formattedHour(syuuseiTarget.taikin_datetime)
      syuuseiData.after.gaisyutsu_datetime = syuuseiData.before.gaisyutsu_datetime = formattedHour(syuuseiTarget.gaisyutsu_datetime)
      syuuseiData.after.modori_datetime = syuuseiData.before.modori_datetime = formattedHour(syuuseiTarget.modori_datetime)
      syuuseiData.after.kyuukei_times = syuuseiData.before.kyuukei_times = getKyuukeReport(syuuseiTarget)
    }
  }

  const setHayabanOsobanData = async (data: HayabanOsoban | null) => {
    if(data){
      hayabanOsoban.hayaban_osoban_id = data.hayaban_osoban_id
      hayabanOsoban.koyou_keiyaku_id = data.koyou_keiyaku_id
      hayabanOsoban.mst_syuugyou_basyo_id = data.mst_syuugyou_basyo_id
      hayabanOsoban.user_id = data.user_id
      hayabanOsoban.target_dakoku_date = data.target_dakoku_date
      hayabanOsoban.start_datetime = data.start_datetime
      hayabanOsoban.end_datetime = data.end_datetime
    }
  }

  const setShinseisData = async (data: DakokuInfoShinsei | null, date: string | Date) => {
    if(data){
      shinseiData.value = {
        hayabanOsoban: data.hayabanOsoban.filter(v => DateUtil.isSame(date, v.date)),
        koukyuu: data.koukyuu.filter(v => DateUtil.isSame(date, v.date)),
        jikangai: data.jikangai.filter(v => DateUtil.isSame(date, v.date)),
        kyuukei: data.kyuukei.filter(v => DateUtil.isSame(date, v.date)),
        furikyuu: data.furikyuu.filter(v => DateUtil.isSame(date, v.date)),
        yuukyuu: data.yuukyuu.filter(v => DateUtil.isSame(date, v.date)),
        kekkin: data.kekkin.filter(v => DateUtil.isSame(date, v.date)),
        soutai: data.soutai.filter(v => DateUtil.isSame(date, v.date)),
        chikoku: data.chikoku.filter(v => DateUtil.isSame(date, v.date)),
        nittyoku: data.nittyoku.filter(v => DateUtil.isSame(date, v.date)),
        kibiki: data.kibiki.filter(v => DateUtil.isSame(date, v.date)),
        hannichiHosei: data.hannichiHosei.filter(v => DateUtil.isSame(date, v.date)),
        syuusei: data.syuusei.filter(v => DateUtil.isSame(date, v.date)),
      }
    }
  }

  const getKyuukeReport = (syuuseiTarget: DakokuDataFull) => {
    if(!syuuseiTarget.dakoku_date){
      return null
    }
    if(!syuuseiTarget.kyuukei_times){
      return null
    }
    if(!syuuseiTarget.gaisyutsu_round_datetime || !syuuseiTarget.modori_round_datetime){
      return syuuseiTarget.kyuukei_times
    }
    if(syuuseiTarget.gaisyutsu_round_datetime >= syuuseiTarget.modori_round_datetime){
      return syuuseiTarget.kyuukei_times
    }

    const dakokuDate = DateUtil.format(syuuseiTarget.dakoku_date)
    const kyuukei = new Date(dakokuDate + ' ' + syuuseiTarget.kyuukei_times).getTime() - new Date(dakokuDate).getTime()
    const gaisyutsuDiff = new Date(syuuseiTarget.modori_round_datetime).getTime() - new Date(syuuseiTarget.gaisyutsu_round_datetime).getTime()
    const result = kyuukei - gaisyutsuDiff

    const hours = result / (1000 * 60 * 60)
    const minutes = (hours - Math.floor(hours)) * 60
    return Math.floor(hours) + ':' + minutes
  }

  const setDataForShinsei = async (syuuseiTarget: DakokuSyuusei | null) => {
    console.log('syuuseiTarget', syuuseiTarget)
    if (!syuuseiTarget) {
      return
    }
    const formattedHour = (datetime: Date | string | null) => {
      if (!datetime) {
        return ''
      }
      if (isString(datetime)) {
        datetime = new Date(datetime);
      }
      return datetime.toLocaleTimeString()
    }

    syuuseiData.after.dakoku_id = syuuseiData.before.dakoku_id = syuuseiTarget.dakoku_id ?? null
    syuuseiData.after.dakoku_date = syuuseiData.before.dakoku_date = syuuseiTarget.dakoku_date ? new Date(syuuseiTarget.dakoku_date).toLocaleDateString() : ''
    syuuseiData.after.syukkin_datetime = syuuseiData.before.syukkin_datetime = formattedHour(syuuseiTarget.syukkin_datetime)
    syuuseiData.after.taikin_datetime = syuuseiData.before.taikin_datetime = formattedHour(syuuseiTarget.taikin_datetime)
    syuuseiData.after.gaisyutsu_datetime = syuuseiData.before.gaisyutsu_datetime = formattedHour(syuuseiTarget.gaisyutsu_datetime)
    syuuseiData.after.modori_datetime = syuuseiData.before.modori_datetime = formattedHour(syuuseiTarget.modori_datetime)
    syuuseiData.after.kyuukei_times = syuuseiData.before.kyuukei_times = syuuseiTarget.kyuukei_times
  }

  const clearData = async () => {
    syuuseiData.before.dakoku_id = syuuseiData.after.dakoku_id = null
    syuuseiData.before.dakoku_date = syuuseiData.after.dakoku_date = ''
    syuuseiData.before.syukkin_datetime = syuuseiData.after.syukkin_datetime = ''
    syuuseiData.before.taikin_datetime = syuuseiData.after.taikin_datetime = ''
    syuuseiData.before.gaisyutsu_datetime = syuuseiData.after.gaisyutsu_datetime = ''
    syuuseiData.before.modori_datetime = syuuseiData.after.modori_datetime = ''
    syuuseiData.before.kyuukei_times = syuuseiData.after.kyuukei_times = ''

    hayabanOsoban.hayaban_osoban_id = null
    hayabanOsoban.koyou_keiyaku_id = null
    hayabanOsoban.mst_syuugyou_basyo_id = null
    hayabanOsoban.user_id = null
    hayabanOsoban.target_dakoku_date = null
    hayabanOsoban.start_datetime = null
    hayabanOsoban.end_datetime = null
  }

  const dakokuSyuuseiModalParts = reactive({
    title: '打刻修正の申請',
    button: ''
  })

  const openDakokuSyuuseiModal = () => {
    dakokuSyuuseiModalRef.value.open()
  }

  const closeDakokuSyuuseiModal = () => {
    dakokuSyuuseiModalRef.value.close()
  }

  return {
    dakokuSyuuseiModalRef,
    dakokuSyuuseiModalParts,
    openDakokuSyuuseiModal,
    closeDakokuSyuuseiModal,
    syuuseiData,
    shinseiData,
    setData,
    setDataForShinsei,
    clearData,
    hasFlow,
    hayabanOsoban,
    setHayabanOsobanData,
    setShinseisData,
  }
}
